import React, { FC, useCallback, useMemo, useState } from 'react';
import { Button, Dialog, Select, styled, Theme } from '@beeline.com/bp-design-system';
import { MixpanelEvents } from 'integrations/mixpanel/Events/MixpanelEvents';
import { useMixpanel } from 'integrations/mixpanel/useMixpanel';
import { ExportEventDetails } from 'integrations/mixpanel/Events/general';
import { useExportsTo } from 'src/utils/hooks/data/exports/useExportsTo';
import { ExportSource, ExportMode } from 'src/utils/hooks/data/exports/utils/Exports.types';
import { OcCsvExportQueryModelInput } from 'src/graphql/types';
import { useRateCardsIntl } from './translations/useRateCardsIntl';

export interface RateCardsExportDialogProps {
  open: boolean;
  onClose: () => void;
}

type Option = { label: string; value: ExportMode };

export const RateCardsExportDialog: FC<React.PropsWithChildren<RateCardsExportDialogProps>> = ({ open, onClose }) => {
  const rateCardsIntl = useRateCardsIntl();
  const { track } = useMixpanel();
  const exportTo = useExportsTo();

  const options: Option[] = useMemo(
    () => [
      { value: 'batch-csv', label: rateCardsIntl.csvFormat },
      { value: 'batch-excel', label: rateCardsIntl.excelFormat }
    ],
    [rateCardsIntl]
  );

  const [fileFormat, setFileFormat] = useState<ExportMode>('batch-csv');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onExport = useCallback(async () => {
    setIsSubmitting(true);
    try {
      track<ExportEventDetails>(MixpanelEvents.general.EXPORT, {
        exportType: fileFormat,
        exportEntity: 'rate_card',
        origin: 'table-tool-bar'
      });

      await exportTo({
        query: { rateCards: {} as OcCsvExportQueryModelInput['rateCards'] },
        mode: fileFormat,
        reportName: 'Rate Cards',
        source: ExportSource.RateCards
      });

      onClose();
    } catch (e: any) {
    } finally {
      setIsSubmitting(false);
    }
  }, [exportTo, fileFormat, onClose, track]);

  return (
    <StyledDialog
      actions={[
        <Button
          key="export"
          color="primary"
          dataTest="rate-cards-export-confirm"
          disabled={isSubmitting}
          onClick={onExport}
          size="medium"
          variant="contained"
        >
          {rateCardsIntl.export}
        </Button>
      ]}
      dataTest="rate-cards-export-dialog"
      onClose={onClose}
      open={open}
      title={rateCardsIntl.exportRateCards}
    >
      <Content>
        <Select
          dataTest="rate-cards-export-format"
          label="File format"
          disabled={isSubmitting}
          fullWidth
          value={fileFormat}
          onChange={(value: ExportMode) => setFileFormat(value)}
          options={options}
        />
      </Content>
    </StyledDialog>
  );
};

const StyledDialog = styled(Dialog)`
  .MuiDialogActions-root {
    padding-top: 24px;
  }
`;

const Content = styled('div')`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }: { theme: Theme }) => theme.spacingSize.m};
`;
